import { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import Loader from '../../../components/globalloaders/Loader';
import FieldLoader from '../../../components/globalloaders/FieldLoader';
import { useLoading } from '../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import {
    Bars3Icon, EllipsisVerticalIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, CurrencyDollarIcon,
    ReceiptPercentIcon, DocumentCurrencyDollarIcon, PresentationChartBarIcon
} from "@heroicons/react/24/outline";
import { expenseTypeOptions, userRoleOptions } from '../../../constants/options';
import { formatLabel, encryptPayload, decryptPayload, formatTableDateTime, toDateTimeLocal, toDateTimeIso, formatCurrency, capitalizeWords } from '../../../constants/helpers';
import ConfirmDialog from '../../../components/dialogbox/ConfirmDialog';
import OpCodeDialog from "../../../components/dialogbox/OpCodeDialog";
import FallBack from '../../../components/dialogbox/FallBack';
import api from '../../../utils/auth/api';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { fetchRiderList } from '../../../utils/helper/apiCallOptions';

const Finance = ({ setIsMobileOpen }) => {

    const { isLoading, setIsLoading } = useLoading();

    const [loaders, setLoaders] = useState({
        fetch: false,
        update: false,
        search: false,
        riderFetch: false,
        statsFetch: false,
    });
    const [dialog, setDialog] = useState({
        open: false,
        type: "info",
        title: "",
        message: "",
        confirmText: "",
        action: () => { },
    });
    const [opCodeDialog, setOpCodeDialog] = useState({
        open: false,
        id: null,
    });

    const [financeData, setFinanceData] = useState([]);
    const [viewMode, setViewMode] = useState("main");

    // Form State used by [Add/Update]
    const initialFormState = {
        role: 'admin',
        rider: '',
        clientAddress: '',
        type: '',
        date: '',
        amount: '',
        note: '',
    };

    // Rider-list State used by [Add/Update]
    const [riderOptions1, setRiderOptions1] = useState([]);

    // Menu State
    const [pageMode, setPageMode] = useState("serviceFee");
    const [showMenu, setShowMenu] = useState(false);
    const attachmentRefs = useRef({});

    // Menu List State
    const [menuState, setMenuState] = useState({
        open: false,
        rowId: null,
        rowData: null,
        position: { top: 0, left: 0 },
    });
    const openMenu = ({ id, position, row }) => {
        setMenuState({
            open: true,
            rowId: id,
            rowData: row,
            position,
        });
    };
    const closeMenu = () => {
        setMenuState({
            open: false,
            rowId: null,
            rowData: null,
            position: { top: 0, left: 0 },
        });
    };

    // For Search Quick & Deep
    const [quickResults, setQuickResults] = useState([]);
    const [deepResults, setDeepResults] = useState([]);
    const [query, setQuery] = useState("");
    const [searchMode, setSearchMode] = useState("quick");
    const [isQuickSearching, setIsQuickSearching] = useState(false);
    const [totalBackendPg, setTotalBackendPg] = useState(1);
    const getDisplayData = () => {
        if (searchMode === "quick") {
            return isQuickSearching ? quickResults : financeData;
        }
        if (searchMode === "deep") {
            return deepResults;
        }
        return financeData;
    };

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const displayData = getDisplayData();
    const [totalPages, setTotalPages] = useState(1);
    const paginatedData = displayData;
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, totalBackendPg);
    useEffect(() => {
        setQuery("");
        setQuickResults([]);
        setDeepResults([]);
        setIsQuickSearching(false);
        if (searchMode === "quick") {
            setCurrentPage(1);
            setTotalPages(Math.ceil(totalBackendPg / itemsPerPage));
        } else if (searchMode === "deep") {
            setCurrentPage(1);
            setTotalPages(1);
        }
    }, [searchMode, totalBackendPg, itemsPerPage]);

    // Update DialogBox with Api Call and Send
    const [isUpdateOpen, setIsUpdateOpen] = useState(false);
    const [formData2, setFormData2] = useState(initialFormState);
    const handleSelectUpdate = (selectedOption, { name }) => {
        setFormData2({
            ...formData2,
            [name]: selectedOption ? selectedOption.value : "",
        });
    };
    const handleNumberUpdate = (e) => {
        const { name, value } = e.target;
        const digits = value.replace(/\D/g, "");
        setFormData2(prev => ({ ...prev, [name]: digits }));
    };
    const handleChangeUpdate = (e) => {
        const { name, value } = e.target;
        const autoCapFields = ["clientAddress"];
        setFormData2({
            ...formData2,
            [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
        });
    };
    const handleUpdateOpen = async (id) => {
        setIsUpdateOpen(true);
        setLoaders(prev => ({ ...prev, update: true }));
        try {
            const response = await api.get(`${process.env.REACT_APP_URI}/finance/detail/${id}/${pageMode}`);
            if (response.data.success) {
                const decryptedData = decryptPayload(response.data.encryptedData);
                setFormData2({
                    id,
                    role: decryptedData?.createdBy?.role || '',
                    rider: decryptedData?.createdBy?.role === 'rider'
                        ? decryptedData.createdBy._id
                        : '',
                    clientAddress: decryptedData?.clientAddress || '',
                    type: decryptedData?.type || '',
                    date: toDateTimeLocal(decryptedData?.date) || '',
                    amount: decryptedData?.amount || '',
                    note: decryptedData?.note || '',
                });
            }
            else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error fetching finance edit:', error);
        } finally {
            setLoaders(prev => ({ ...prev, update: false }));
            return;
        }
    };
    const handleUpdateClose = () => {
        setIsUpdateOpen(false);
        setFormData2(initialFormState);
        setRiderOptions1([]);
    };
    // Update API
    const handleUpdate = async (id) => {
        if (isLoading) return;

        if (!id) {
            toast.error('Unable to update: ID is missing');
            return;
        };
        if (formData2.role === "rider" && !formData2.rider) {
            toast.warning("Rider is required.");
            return;
        }
        if (pageMode === "serviceFee" && !formData2.clientAddress) {
            toast.warning("Client Address is required.");
            return;
        }
        if (pageMode === "expense" && !formData2.type) {
            toast.warning("Expense Type is required.");
            return;
        }
        if (!formData2.date) {
            toast.warning("Date is required.");
            return;
        }
        if (!formData2.amount) {
            toast.warning("Amount is required.");
            return;
        }

        const payload = {
            date: toDateTimeIso(formData2.date),
            amount: Number(formData2.amount),
            ...(pageMode === "serviceFee" && { clientAddress: formData2.clientAddress }),
            ...(pageMode === "expense" && { type: formData2.type, note: formData2.note?.trim() || null }),
            ...(formData2.role === "rider" && { rider: formData2.rider }),
        };
        const encryptedData = encryptPayload(payload);

        setIsLoading(true);
        try {
            const response = await api.put(`${process.env.REACT_APP_URI}/finance/update/${id}/${pageMode}`, { data: encryptedData });
            if (response.data.success) {
                const decryptedData = decryptPayload(response.data.encryptedData);
                setFinanceData((prevData) => prevData.map((data) => data._id === id ? { ...data, ...decryptedData } : data));
                toast.success(response.data.message);
                handleUpdateClose();
            } else {
                toast.error(response.data.message);
            }
        }
        catch (error) {
            console.log("Finance Updating Data failed:", error);
            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    data.errors.forEach(err => toast.warning(err));
                } else {
                    toast.error(data.message);
                }
            } else {
                toast.error("Unable to Update Finance Network Error, please try again!");
            }
        }
        finally {
            setIsLoading(false);
            return;
        }
    };

    // QUICK SEARCH (local state)
    const handleQuickSearch = (e) => {
        const value = e.target.value;
        setQuery(value);
        if (searchMode === "quick") {
            if (value.trim() === "") {
                setIsQuickSearching(false);
                setQuickResults(financeData);
            } else {
                setIsQuickSearching(true);
                let results
                if (pageMode === "serviceFee") {
                    results = financeData.filter((s) =>
                        s.clientAddress?.toLowerCase().includes(value.toLowerCase()) ||
                        s.serviceFeeId?.toLowerCase().includes(value.toLowerCase())
                    );
                } else if (pageMode === "expense") {
                    results = financeData.filter((s) =>
                        s.type.toLowerCase().includes(value.toLowerCase()) ||
                        s.expenseId?.toLowerCase().includes(value.toLowerCase())
                    );
                } else {
                    results = financeData;
                }
                setQuickResults(results);
            }
        }
    };
    // DEEP SEARCH (API)
    const handleDeepSearch = async (searchQuery) => {
        if (!searchQuery.trim()) {
            toast.warning("Enter value to search");
            return;
        }
        setLoaders(prev => ({ ...prev, search: true }));
        try {
            const response = await api.get(`${process.env.REACT_APP_URI}/finance/search/${pageMode}`, {
                params: { value: searchQuery },
            });
            if (response.data.success) {
                const decryptedData = decryptPayload(response.data.encryptedData);
                setDeepResults(decryptedData);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error("Error deep searching finance:", error);
            toast.error(error?.response.data.message || "Deep search finance failed!");
        } finally {
            setLoaders(prev => ({ ...prev, search: false }));
            return;
        }
    };
    // Connect Search to Quick or Deep via searchMode
    const handleSearchSubmit = (e) => {
        if (e) e.preventDefault();
        if (searchMode === "quick") {
            handleQuickSearch({ target: { value: query } });
        } else if (searchMode === "deep") {
            handleDeepSearch(query);
        }
    };

    // Fetch All API
    useEffect(() => {
        const fetchData = async (page = 1) => {
            setLoaders(prev => ({ ...prev, fetch: true }));
            try {
                const response = await api.get(`${process.env.REACT_APP_URI}/finance/get-all`, {
                    params: { page, limit: itemsPerPage, mode: pageMode },
                });
                if (response.data.success) {
                    const decryptedData = decryptPayload(response.data.encryptedData);
                    setFinanceData(decryptedData.data);
                    setQuickResults(decryptedData.data);
                    setTotalBackendPg(decryptedData.total);
                    setTotalPages(Math.ceil(decryptedData.total / itemsPerPage));
                }
                else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                console.error('Error fetching finances:', error);
            } finally {
                setLoaders(prev => ({ ...prev, fetch: false }));
                return;
            }
        };
        fetchData(currentPage);
    }, [currentPage, pageMode]);

    // Delete API By ID
    const handleDelete = async (id, opcode) => {
        if (isLoading) return;
        setIsLoading(true);
        const payload = encryptPayload({ id, opcode, mode: pageMode });
        try {
            const response = await api.delete(`${process.env.REACT_APP_URI}/finance/delete`, { data: { data: payload } });
            if (response.data.success) {
                setFinanceData((prevData) => prevData.filter((data) => data._id !== id));
                toast.success(response.data.message);
            }
        }
        catch (error) {
            console.log("Error occur while deleting finance:", error);
            if (error.response && error.response.data) {
                const data = error.response.data;
                toast.error(data.message);
            } else {
                toast.error("Unable to Delete Finance Network Error, please try again!");
            }
        }
        finally {
            setIsLoading(false);
            return;
        }
    };

    // Add DialogBox Code & Store API
    const [formData, setFormData] = useState(initialFormState);
    const [isAddDialog, setAddDialog] = useState(false);
    const handleAddOpen = () => {
        setAddDialog(true);
    };
    const handleAddClose = () => {
        setAddDialog(false);
        setFormData(initialFormState);
        setRiderOptions1([]);
    };
    const handleSelectChange = (selectedOption, { name }) => {
        setFormData({
            ...formData,
            [name]: selectedOption ? selectedOption.value : "",
        });
    };
    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const digits = value.replace(/\D/g, "");
        setFormData(prev => ({ ...prev, [name]: digits }));
    };
    const onChange = (e) => {
        const { name, value } = e.target;
        const autoCapFields = ["clientAddress"];
        setFormData({
            ...formData,
            [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
        });
    };
    const handleSubmit = async () => {
        if (isLoading) return;

        // --- Required field checks ---
        if (formData.role === "rider" && !formData.rider) {
            toast.warning("Rider is required.");
            return;
        }
        if (pageMode === "serviceFee" && !formData.clientAddress) {
            toast.warning("Client Address is required.");
            return;
        }
        if (pageMode === "expense" && !formData.type) {
            toast.warning("Expense Type is required.");
            return;
        }
        if (!formData.date) {
            toast.warning("Date is required.");
            return;
        }
        if (!formData.amount) {
            toast.warning("Amount is required.");
            return;
        }

        const payload = {
            date: toDateTimeIso(formData.date),
            amount: Number(formData.amount),
            ...(pageMode === "serviceFee" && { clientAddress: formData.clientAddress }),
            ...(pageMode === "expense" && { type: formData.type, note: formData.note?.trim() || null }),
            ...(formData.role === "rider" && { rider: formData.rider }),
        };
        const encryptedData = encryptPayload(payload);

        setIsLoading(true);
        try {
            const response = await api.post(`${process.env.REACT_APP_URI}/finance/create/${pageMode}`, { data: encryptedData });
            if (response.data.success) {
                toast.success(response.data.message);
                const decryptedData = decryptPayload(response.data.encryptedData);
                setFinanceData((prevData) => [decryptedData, ...prevData]);
                handleAddClose();
            }
            else {
                toast.error(response.data.message);
            }
        }
        catch (error) {
            console.log("Finance Creation failed:", error);
            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    data.errors.forEach(err => toast.warning(err));
                } else {
                    toast.error(data.message);
                }
            } else {
                toast.error("Unable to Create Finance Network Error, please try again!");
            }
        }
        finally {
            setIsLoading(false);
            return;
        }
    };

    // Rider-list Fetch API used by [Add/Update]
    useEffect(() => {
        if (formData.role !== "rider" && formData2.role !== "rider") {
            setRiderOptions1([]);
            return;
        }
        const fetchData = async () => {
            const data = await fetchRiderList(setLoaders);
            setRiderOptions1(data);
        };
        fetchData();
    }, [formData.role, formData2.role]);

    // Stats States & API
    const [statsData, setStatsData] = useState([]);
    const [statsMode, setStatsMode] = useState("currentMonth");
    const statsDisplay = statsData[statsMode];
    useEffect(() => {
        const fetchData = async () => {
            if (viewMode !== "overview") {
                setStatsData([]);
                setStatsMode("currentMonth");
                return;
            }
            setLoaders(prev => ({ ...prev, statsFetch: true }));
            try {
                const response = await api.get(`${process.env.REACT_APP_URI}/finance/stats`);
                if (response.data.success) {
                    const decryptedData = decryptPayload(response.data.encryptedData);
                    setStatsData(decryptedData);
                }
                else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                console.error('Error fetching finance stats data:', error);
            } finally {
                setLoaders(prev => ({ ...prev, statsFetch: false }));
                return;
            }
        };
        fetchData();
    }, [viewMode]);

    // PDF PaySlip States, APIs & Template
    const [riderOptions, setRiderOptions] = useState([]);
    const [pdfData, setPdfData] = useState([]);
    const [pdfFilters, setPdfFilters] = useState({
        rider: "",
        month: "",
        year: "",
    });
    const handlePdfFilterChange = (e) => {
        const { name, value } = e.target;
        setPdfFilters((prev) => ({ ...prev, [name]: value }));
    };
    const handlePdfSelectChange = (selectedOption, { name }) => {
        setPdfFilters((prev) => ({
            ...prev,
            [name]: selectedOption ? selectedOption.value : "",
        }));
    };
    useEffect(() => {
        if (viewMode !== "overview") {
            setRiderOptions([]);
            setPdfData([]);
            setPdfFilters({
                rider: "",
                month: "",
                year: "",
            });
            return;
        }
        const fetchData = async () => {
            const data = await fetchRiderList(setLoaders);
            setRiderOptions(data);
        };
        fetchData();
    }, [viewMode]);
    const handlePdfFetch = async () => {
        if (isLoading) return;
        if (!pdfFilters.rider || !pdfFilters.month || !pdfFilters.year) {
            toast.warning("Select Rider, Month & Year");
            return;
        }
        const payload = encryptPayload(pdfFilters);
        setIsLoading(true);
        try {
            const response = await api.post(`${process.env.REACT_APP_URI}/finance/payslip`, { date: payload });
            if (response.data.success) {
                const decrypted = decryptPayload(response.data.encryptedData);
                setPdfData(decrypted);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error fetching pdf finance data:', error);
            toast.error(error?.response.data.message || "Failed to fetch PDF data!");
        } finally {
            setIsLoading(false);
            return;
        }
    };
    const handlePayslipPDF = async (pdfData) => {

        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage([595.28, 841.89]);
        const { width, height } = page.getSize();
        const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);

        // Color scheme
        const primaryColor = rgb(0.11, 0.33, 0.66);
        const secondaryColor = rgb(0.2, 0.2, 0.2);

        // Helper functions
        const drawSectionTitle = (text, y) => {
            page.drawText(text, {
                x: 50,
                y,
                size: 14,
                font: fontBold,
                color: primaryColor,
            });
            return y - 25;
        };
        const drawDetailRow = (label, value, y) => {
            page.drawText(label, {
                x: 50,
                y,
                size: 12,
                font: fontRegular,
                color: secondaryColor,
            });
            page.drawText(value, {
                x: width - 410,
                y,
                size: 12,
                font: fontBold,
                color: secondaryColor,
            });
            return y - 20;
        };

        // Header Section
        page.drawRectangle({
            x: 0,
            y: height - 100,
            width,
            height: 80,
            color: primaryColor,
        });
        page.drawText("MBEO PaySlip", {
            x: 50,
            y: height - 60,
            size: 24,
            font: fontBold,
            color: rgb(1, 1, 1),
        });
        page.drawText("Official Salary Statement", {
            x: 50,
            y: height - 90,
            size: 12,
            font: fontRegular,
            color: rgb(0.9, 0.9, 0.9),
        });

        // Company Info
        let yPosition = height - 150;
        yPosition = drawSectionTitle("Company Information", yPosition);
        yPosition = drawDetailRow("Name:", "Malik Bilal Employment Office", yPosition);
        yPosition = drawDetailRow("Address:", "Main Kamahan Road, Munir Housing Society, Lahore, Punjab, PK.", yPosition);
        yPosition = drawDetailRow("Contact:", "abid70700@gmail.com | 0300 8021407, 0300 4714768", yPosition);

        // Employee Details
        yPosition -= 20;
        yPosition = drawSectionTitle("Employee Details", yPosition);
        yPosition = drawDetailRow("ID · Role:", `${pdfData.rider?.userId} · ${formatLabel(pdfData.rider?.role)}`, yPosition);
        yPosition = drawDetailRow("Name:", pdfData.rider?.employeeRef?.name, yPosition);
        yPosition = drawDetailRow("Salary Month/Year:", `${pdfData.month} / ${pdfData.year}`, yPosition);

        // Financial Summary Table
        yPosition -= 40;
        const tableTop = yPosition;

        // Table Header
        page.drawRectangle({
            x: 50,
            y: tableTop,
            width: width - 100,
            height: 30,
            color: primaryColor,
        });
        page.drawText("Description", {
            x: 60,
            y: tableTop + 8,
            size: 12,
            font: fontBold,
            color: rgb(1, 1, 1),
        });
        page.drawText("Amount", {
            x: width - 140,
            y: tableTop + 8,
            size: 12,
            font: fontBold,
            color: rgb(1, 1, 1),
        });

        // Table Rows
        const addTableRow = (label, amount, y, color = secondaryColor) => {
            page.drawText(label, {
                x: 60,
                y: y + 8,
                size: 12,
                font: fontRegular,
                color,
            });

            page.drawText(amount, {
                x: width - 140,
                y: y + 8,
                size: 12,
                font: fontRegular,
                color,
            });

            page.drawLine({
                start: { x: 50, y: y - 5 },
                end: { x: width - 50, y: y - 5 },
                thickness: 0.5,
                color: rgb(0.8, 0.8, 0.8),
            });
        };

        let currentY = tableTop - 30;
        addTableRow("Net Balance", `PKR ${formatCurrency(pdfData.totalNetBalance) || 0}`, currentY, primaryColor);
        currentY -= 30;
        addTableRow("Total Salary", `PKR ${formatCurrency(pdfData.rider?.salary) || 0}`, currentY, primaryColor);
        currentY -= 30;
        addTableRow("Payable Salary", `PKR ${formatCurrency(pdfData.salaryPayable) || 0}`, currentY, primaryColor);
        currentY -= 30;
        addTableRow("Company Receivable", `PKR ${formatCurrency(pdfData.companyReceivable) || 0}`, currentY, primaryColor);

        // Footer
        page.drawText(`Generated by MBEOWS | ${new Date().toLocaleString('en-PK')}`, {
            x: 50,
            y: 40,
            size: 10,
            font: fontRegular,
            color: secondaryColor,
        });

        // Save PDF
        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        saveAs(blob, `MBEO_PaySlip_[${pdfData.rider?.employeeRef?.name}-(${pdfData.month}-${pdfData.year})].pdf`);
    };

    // Skeleton Loader
    const SkeletonCard = ({ height = 20, width = "full", rounded = "2xl" }) => (
        <div className={`animate-pulse bg-gray-100 rounded-${rounded} w-${width} h-${height}`} ></div>
    );

    // Fallback control state
    const hasUpdateData = formData2?.id;

    // Close menu list on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                showMenu &&
                attachmentRefs.current &&
                !attachmentRefs.current.contains(e.target)
            ) {
                setShowMenu(null);
            }
        };
        const closeOnKey = () => setShowMenu(null);
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", closeOnKey);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", closeOnKey);
        };
    }, [showMenu]);
    useEffect(() => {
        if (!menuState.open) return;
        const handleOutside = (e) => {
            const isTrigger = e.target.closest('[data-menu-trigger="true"]');
            const isMenu = e.target.closest('[data-floating-menu="true"]');
            if (isTrigger || isMenu) return;
            closeMenu();
        };
        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("keydown", closeMenu);
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("keydown", closeMenu);
        };
    }, [menuState.open]);

    // actions btn list
    const menuActionList = [
        {
            label: "Edit", type: "edit", color: "text-blue-600", hover: "hover:bg-blue-100",
            boxType: "info", message: `Do you want to edit this ${pageMode === "serviceFee" ? "Service Fee" : "Expense"} details?`,
        },
        {
            label: "Delete", type: "delete", color: "text-red-600", hover: "hover:bg-red-100",
            boxType: "danger",
            message: `This will permanently delete the ${pageMode === "serviceFee" ? "Service Fee" : "Expense"}. This action cannot be undone.`,
        },
    ];

    return (
        <>
            <ConfirmDialog
                isOpen={dialog.open}
                onClose={() => setDialog((d) => ({ ...d, open: false }))}
                onConfirm={dialog.action}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                type={dialog.type}
            />
            <OpCodeDialog
                isOpen={opCodeDialog.open}
                type="danger"
                title="Process Authorization"
                message="By proceeding, you accept responsibility for any resulting data loss or modification. Please enter the OpCode to continue."
                confirmText="Verify"
                onClose={() =>
                    setOpCodeDialog({ open: false, id: null })
                }
                onConfirm={(code) => {
                    handleDelete(opCodeDialog.id, code);
                }}
            />
            {/* Three Dot Menu UI */}
            {menuState.open && (
                <div className="fixed inset-0 z-50 pointer-events-none">
                    <div
                        data-floating-menu="true"
                        className="absolute bg-white border shadow-xl rounded-xl p-2 w-44 space-y-1 pointer-events-auto"
                        style={{
                            top: menuState.position.top,
                            left: menuState.position.left,
                        }}
                    >
                        {menuActionList.map((action) => (
                            <button
                                key={action.type}
                                className={`flex items-center w-full gap-2 p-2 rounded-lg ${action.hover} text-sm font-medium cursor-pointer`}
                                onClick={() => {
                                    closeMenu();
                                    if (action.type === "edit") {
                                        setDialog({
                                            open: true,
                                            type: action.boxType,
                                            title: `Edit ${pageMode === "serviceFee" ? "Service Fee" : "Expense"}`,
                                            message: action.message,
                                            confirmText: action.label,
                                            action: () => handleUpdateOpen(menuState.rowId),
                                        });
                                        return;
                                    }
                                    setDialog({
                                        open: true,
                                        type: action.boxType,
                                        title: `Delete ${pageMode === "serviceFee" ? "Service Fee" : "Expense"}`,
                                        message: action.message,
                                        confirmText: "Proceed",
                                        action: () => {
                                            setOpCodeDialog({
                                                open: true,
                                                id: menuState.rowId,
                                            });
                                        },
                                    });
                                }}
                            >
                                <span className={action.color}>{action.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {/* Update UI */}
            {isUpdateOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
                    <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
                        <div className="max-h-[85vh] flex flex-col">
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                                <h2 className="text-xl sm:text-2xl font-bold">Update {pageMode === "serviceFee" ? "Service Fee" : "Expense"}</h2>
                                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
                            </div>
                            {loaders.update ? (
                                <div className="flex justify-center items-center h-[45vh]">
                                    <Loader />
                                </div>
                            ) : !hasUpdateData ? (
                                <FallBack
                                    title={`Unable to load ${pageMode === "serviceFee" ? "service fee" : "expense"} for update`}
                                    message="The data could not be loaded for editing. Please refresh or try again."
                                    actionText="Close"
                                    onAction={handleUpdateClose}
                                />
                            ) : (
                                <div className="flex-1 overflow-auto p-3 sm:p-6 bg-gray-50">
                                    <fieldset className="bg-white border p-4 rounded-lg">
                                        <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Info</legend>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Role
                                                </label>
                                                <div className="flex rounded-full bg-gray-200 p-1 w-fit">
                                                    {userRoleOptions.map((option) => {
                                                        const isActive = formData2.role === option.value;
                                                        return (
                                                            <button
                                                                key={option.value}
                                                                type="button"
                                                                onClick={() =>
                                                                    setFormData2((prev) => ({
                                                                        ...prev,
                                                                        role: option.value,
                                                                        rider: option.value === "admin" ? "" : prev.rider,
                                                                    }))
                                                                }
                                                                className={`px-4 py-1 text-sm rounded-full transition ${isActive ? "bg-white shadow text-black" : "text-gray-600"
                                                                    }`}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {formData2.role === "rider" && (
                                                <div>
                                                    {loaders.riderFetch ? (
                                                        <FieldLoader label="Rider" />
                                                    ) : (
                                                        <>
                                                            <label className="block text-sm font-medium text-gray-700">
                                                                Rider
                                                            </label>
                                                            <Select
                                                                name="rider"
                                                                options={riderOptions1}
                                                                value={riderOptions1.find(option => option.value === formData2.rider) || null}
                                                                placeholder="Select Rider"
                                                                onChange={handleSelectUpdate}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {pageMode === "serviceFee" && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Client Address</label>
                                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="H#01, ST#01, ABC..."
                                                        type="text"
                                                        name='clientAddress'
                                                        value={formData2.clientAddress}
                                                        onChange={handleChangeUpdate}
                                                    />
                                                </div>
                                            )}

                                            {pageMode === "expense" && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Type</label>
                                                    <Select
                                                        name="type"
                                                        options={expenseTypeOptions}
                                                        placeholder="Select Type"
                                                        value={expenseTypeOptions.find(option => option.value === formData2.type)}
                                                        onChange={(selectedOption) =>
                                                            handleSelectUpdate(selectedOption, { name: 'type' })
                                                        }
                                                    />
                                                </div>
                                            )}

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Date</label>
                                                <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                    type="datetime-local"
                                                    name='date'
                                                    value={formData2.date}
                                                    onChange={handleChangeUpdate}
                                                />
                                            </div>

                                            <div className="space-y-1">
                                                <label className="block text-sm font-medium text-gray-700">Amount</label>
                                                <div className="relative">
                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                                        PKR
                                                    </span>
                                                    <input
                                                        className="w-full pl-12 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="X,XXX"
                                                        type="text"
                                                        name="amount"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        value={formatCurrency(formData2.amount)}
                                                        onChange={handleNumberUpdate}
                                                    />
                                                </div>
                                            </div>

                                            {pageMode === "expense" && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Note</label>
                                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        type="text"
                                                        placeholder='(Optional)'
                                                        name='note'
                                                        value={formData2.note}
                                                        onChange={handleChangeUpdate}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </fieldset>
                                </div>
                            )}
                            {!loaders.update && hasUpdateData && (
                                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                                    <button
                                        type='button'
                                        onClick={() => handleUpdateClose()}
                                        className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDialog({
                                            open: true,
                                            title: "Confirm Update",
                                            message: `Are you sure you want to update this ${pageMode === "serviceFee" ? "service fee" : "expense"} information? Changes will be saved immediately.`,
                                            confirmText: "Update",
                                            type: "warning",
                                            action: () => handleUpdate(formData2.id),
                                        })}
                                        className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                                    >
                                        Update
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className="w-full">
                {/* Heading */}
                <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        <button
                            className="md:hidden p-2 bg-white shadow rounded-lg"
                            onClick={() => setIsMobileOpen(true)}
                        >
                            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
                        </button>
                        <div className="flex flex-wrap items-center">
                            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                                Fina
                            </h1>
                            <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                                nce
                            </span>
                        </div>
                    </div>

                    <div className='flex items-center gap-2'>
                        {viewMode === "main" && (
                            <button
                                className="px-4 sm:px-6 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 font-medium text-sm sm:text-base"
                                onClick={handleAddOpen}
                            >
                                Add
                            </button>
                        )}
                        {isAddDialog && (
                            <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
                                <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
                                    <div className="max-h-[85vh] flex flex-col">
                                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                                            <h2 className="text-xl sm:text-2xl font-bold">Create {pageMode === "serviceFee" ? "Service Fee" : "Expense"}</h2>
                                            <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
                                        </div>
                                        <div className="flex-1 overflow-auto p-3 sm:p-6 bg-gray-50">
                                            <fieldset className="bg-white border p-4 rounded-lg">
                                                <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Info</legend>
                                                <div className="grid grid-cols-1 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Role
                                                        </label>
                                                        <div className="flex rounded-full bg-gray-200 p-1 w-fit">
                                                            {userRoleOptions.map((option) => {
                                                                const isActive = formData.role === option.value;
                                                                return (
                                                                    <button
                                                                        key={option.value}
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setFormData((prev) => ({
                                                                                ...prev,
                                                                                role: option.value,
                                                                                rider: option.value === "admin" ? "" : prev.rider,
                                                                            }))
                                                                        }
                                                                        className={`px-4 py-1 text-sm rounded-full transition ${isActive ? "bg-white shadow text-black" : "text-gray-600"
                                                                            }`}
                                                                    >
                                                                        {option.label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                    {formData.role === "rider" && (
                                                        <div>
                                                            {loaders.riderFetch ? (
                                                                <FieldLoader label="Rider" />
                                                            ) : (
                                                                <>
                                                                    <label className="block text-sm font-medium text-gray-700">
                                                                        Rider
                                                                    </label>
                                                                    <Select
                                                                        name="rider"
                                                                        options={riderOptions1}
                                                                        value={riderOptions1.find(option => option.value === formData.rider) || null}
                                                                        placeholder="Select Rider"
                                                                        onChange={handleSelectChange}
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    )}

                                                    {pageMode === "serviceFee" && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Client Address</label>
                                                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                                placeholder="H#01, ST#01, ABC..."
                                                                type="text"
                                                                name='clientAddress'
                                                                value={formData.clientAddress}
                                                                onChange={onChange}
                                                            />
                                                        </div>
                                                    )}

                                                    {pageMode === "expense" && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Type</label>
                                                            <Select
                                                                name="type"
                                                                options={expenseTypeOptions}
                                                                placeholder="Select Type"
                                                                value={expenseTypeOptions.find(option => option.value === formData.type)}
                                                                onChange={(selectedOption) =>
                                                                    handleSelectChange(selectedOption, { name: 'type' })
                                                                }
                                                            />
                                                        </div>
                                                    )}

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">Date</label>
                                                        <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                            type="datetime-local"
                                                            name='date'
                                                            value={formData.date}
                                                            onChange={onChange}
                                                        />
                                                    </div>

                                                    <div className="space-y-1">
                                                        <label className="block text-sm font-medium text-gray-700">Amount</label>
                                                        <div className="relative">
                                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                                                PKR
                                                            </span>
                                                            <input
                                                                className="w-full pl-12 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                                placeholder="X,XXX"
                                                                type="text"
                                                                name="amount"
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                value={formatCurrency(formData.amount)}
                                                                onChange={handleNumberChange}
                                                            />
                                                        </div>
                                                    </div>

                                                    {pageMode === "expense" && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Note</label>
                                                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                                type="text"
                                                                placeholder='(Optional)'
                                                                name='note'
                                                                value={formData.note}
                                                                onChange={onChange}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </fieldset>
                                        </div>
                                        <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                                            <button
                                                type='button'
                                                onClick={() => handleAddClose()}
                                                className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                                            >
                                                Close
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSubmit}
                                                className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                                            >
                                                Create
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={attachmentRefs} className="relative flex-shrink-0">
                            <button
                                type="button"
                                onClick={(e) => setShowMenu(prev => !prev)}
                                className="p-2 rounded-full hover:bg-gray-100"
                            >
                                <EllipsisVerticalIcon className="h-6 w-6 text-gray-600" />
                            </button>
                            {showMenu && (
                                <div className="absolute top-10 right-0 bg-white border shadow-lg rounded-xl p-3 w-44 z-10 space-y-3">
                                    <div className="flex flex-col space-y-1">
                                        <div className="text-xs font-bold text-gray-500">View Mode</div>
                                        <button
                                            className={`w-full text-left px-2 py-1 rounded-lg text-sm font-medium transition ${viewMode === "main" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                                            onClick={() => {
                                                setViewMode("main");
                                                setShowMenu(false);
                                            }}
                                        >
                                            Main
                                        </button>
                                        <button
                                            className={`w-full text-left px-2 py-1 rounded-lg text-sm font-medium transition ${viewMode === "overview" ? "bg-green-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                                            onClick={() => {
                                                setViewMode("overview");
                                                setShowMenu(false);
                                            }}
                                        >
                                            Overview
                                        </button>
                                    </div>
                                    {viewMode === "main" && (
                                        <>
                                            <hr className="border-t border-gray-200" />
                                            <div className="flex flex-col space-y-1">
                                                <div className="text-xs font-bold text-gray-500">Page Mode</div>
                                                <button
                                                    className={`flex items-center gap-1 w-full px-2 py-1 rounded-lg text-sm font-medium transition ${pageMode === "serviceFee" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                                                    onClick={() => {
                                                        setPageMode("serviceFee");
                                                        setShowMenu(false);
                                                    }}
                                                >
                                                    <CurrencyDollarIcon className="h-4 w-4" />
                                                    Service Fee
                                                </button>
                                                <button
                                                    className={`flex items-center gap-1 w-full px-2 py-1 rounded-lg text-sm font-medium transition ${pageMode === "expense" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                                                    onClick={() => {
                                                        setPageMode("expense");
                                                        setShowMenu(false);
                                                    }}
                                                >
                                                    <ReceiptPercentIcon className="h-4 w-4" />
                                                    Expense
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <hr className="border-t border-gray-200 my-4" />

                {/* Overview */}
                {viewMode === "overview" && (
                    <div className="space-y-8">
                        {loaders.statsFetch ? (
                            <div className="p-6 rounded-3xl bg-gray-300 shadow-md border border-gray-200">
                                <SkeletonCard height={6} width="1/3" rounded="xl" />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                    {[1, 2, 3, 4].map((_, j) => (
                                        <SkeletonCard key={j} height={16} width="full" rounded="2xl" />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-3xl shadow-md p-6 border border-gray-100">
                                <div className="flex sm:flex-row flex-col sm:justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <PresentationChartBarIcon className="h-6 w-6 text-emerald-600" />
                                        <h3 className="text-lg font-semibold text-gray-800">
                                            Overview ({statsMode === "last90Days" ? "Last 90 Days" : "Current Month"})
                                        </h3>
                                    </div>

                                    <div className="flex justify-end mt-2 sm:mt-0">
                                        <button
                                            onClick={() =>
                                                setStatsMode(statsMode === "last90Days" ? "currentMonth" : "last90Days")
                                            }
                                            className="px-2 sm:px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs sm:text-sm font-medium"
                                        >
                                            {statsMode === "last90Days" ? "Current" : "Recent"}
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="bg-emerald-50 rounded-2xl p-5 text-center">
                                        <p className="text-xs text-gray-600">Income</p>
                                        <p className="text-2xl font-bold text-emerald-600">
                                            PKR {formatCurrency(statsDisplay?.totalIncome) || 0}
                                        </p>
                                    </div>

                                    <div className="bg-yellow-50 rounded-2xl p-5 text-center">
                                        <p className="text-xs text-gray-600">Service Fee</p>
                                        <p className="text-2xl font-bold text-yellow-600">
                                            PKR {formatCurrency(statsDisplay?.totalServiceFee) || 0}
                                        </p>
                                    </div>

                                    <div className="bg-red-50 rounded-2xl p-5 text-center">
                                        <p className="text-xs text-gray-600">Expense</p>
                                        <p className="text-2xl font-bold text-red-500">
                                            PKR {formatCurrency(statsDisplay?.totalExpense) || 0}
                                        </p>
                                    </div>

                                    <div className="bg-blue-50 rounded-2xl p-5 text-center">
                                        <p className="text-xs text-gray-600">Net Balance</p>
                                        <p className="text-2xl font-bold text-blue-600">
                                            PKR {formatCurrency(statsDisplay?.netBalance) || 0}
                                        </p>
                                        <p className="text-[10px] text-gray-400 mt-1">
                                            (Service Fee – Expense)
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="bg-white rounded-3xl shadow-md mb-7 p-4 space-y-5 border border-gray-100">
                            <div className="flex sm:flex-row flex-col sm:justify-between">
                                <div className="flex items-center gap-2">
                                    <DocumentCurrencyDollarIcon className="h-6 w-6 text-emerald-600" />
                                    <h3 className="text-lg font-semibold text-gray-800">
                                        Rider PaySlip
                                    </h3>
                                </div>
                            </div>

                            <div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        {loaders.riderFetch ? (
                                            <FieldLoader label="Rider" />
                                        ) : (
                                            <>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Rider
                                                </label>
                                                <Select
                                                    name="rider"
                                                    options={riderOptions}
                                                    value={riderOptions.find(option => option.value === pdfFilters.rider) || null}
                                                    placeholder="Select Rider"
                                                    onChange={handlePdfSelectChange}
                                                />
                                            </>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Month</label>
                                        <select
                                            name="month"
                                            value={pdfFilters.month}
                                            onChange={handlePdfFilterChange}
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                                        >
                                            <option value="">Select Month</option>
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i + 1} value={i + 1}>
                                                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Year</label>
                                        <select
                                            name="year"
                                            value={pdfFilters.year}
                                            onChange={handlePdfFilterChange}
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                                        >
                                            <option value="">Select Year</option>
                                            {Array.from({ length: 5 }, (_, i) => {
                                                const year = new Date().getFullYear() - i;
                                                return (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex justify-between mt-3">
                                    <button
                                        className={`px-4 sm:px-6 py-2 rounded-lg text-sm sm:text-base text-white transition
                                    ${pdfData?.rider
                                                ? "bg-green-500 hover:bg-green-600 active:scale-95"
                                                : "bg-gray-300"}
                                    `}
                                        disabled={!pdfData?.rider}
                                        onClick={() => handlePayslipPDF(pdfData)}
                                    >
                                        PDF
                                    </button>
                                    <button
                                        className={`px-4 sm:px-6 py-2 rounded-lg text-sm sm:text-base text-white transition
                                    ${pdfFilters.rider && pdfFilters.month && pdfFilters.year
                                                ? "bg-blue-600 hover:bg-blue-500 active:scale-95"
                                                : "bg-gray-300"}
                                    `}
                                        disabled={!pdfFilters.rider || !pdfFilters.month || !pdfFilters.year}
                                        onClick={() => handlePdfFetch()}
                                    >
                                        Filter
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                <div className="bg-yellow-50 rounded-2xl p-5 text-center">
                                    <p className="text-xs text-gray-600">Service Fee</p>
                                    <p className="text-2xl font-bold text-yellow-600">
                                        PKR {formatCurrency(pdfData.totalServiceFee) || 0}
                                    </p>
                                </div>

                                <div className="bg-red-50 rounded-2xl p-5 text-center">
                                    <p className="text-xs text-gray-600">Expense</p>
                                    <p className="text-2xl font-bold text-red-500">
                                        PKR {formatCurrency(pdfData.totalExpense) || 0}
                                    </p>
                                </div>

                                <div className="bg-blue-50 rounded-2xl p-5 text-center">
                                    <p className="text-xs text-gray-600">Net Balance</p>
                                    <p className="text-2xl font-bold text-blue-600">
                                        PKR {formatCurrency(pdfData.totalNetBalance) || 0}
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-1">
                                        (Service Fee – Expense)
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Data Display Code */}
                {viewMode === "main" && (
                    <div className='w-full'>
                        {/* Quick & Deep Search Bar */}
                        <div className="w-full flex justify-center mb-7">
                            <form
                                onSubmit={handleSearchSubmit}
                                className="relative w-full max-w-2xl flex items-center"
                            >
                                {/* Search Input */}
                                <div className="relative flex-1">
                                    <input
                                        type="search"
                                        placeholder={`${searchMode === "quick" ? "Quick" : "Deep"} search . . .`}
                                        value={query}
                                        onChange={handleQuickSearch}
                                        className="w-full py-3 pl-10 sm:pl-12 pr-32 sm:pr-40 rounded-2xl bg-white/80 backdrop-blur-sm 
                             border border-gray-200 shadow-md focus:ring-2 focus:ring-indigo-400 
                             focus:outline-none text-gray-700 placeholder-gray-400 transition"
                                    />
                                    <MagnifyingGlassIcon className="absolute w-5 h-5 left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                </div>
                                {/* Mode Toggle */}
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex rounded-full overflow-hidden border border-gray-200 shadow">
                                    <button
                                        type="button"
                                        onClick={() => setSearchMode("quick")}
                                        className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "quick"
                                            ? "bg-blue-600 text-white"
                                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                                            }`}
                                    >
                                        Quick
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSearchMode("deep")}
                                        className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "deep"
                                            ? "bg-indigo-600 text-white"
                                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                                            }`}
                                    >
                                        Deep
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Table Container */}
                        <div className="overflow-x-auto h-[70vh]">
                            {(loaders.fetch || loaders.search) ? (
                                <div className="flex justify-center items-center h-full p-6">
                                    <Loader />
                                </div>
                            ) : displayData.length === 0 ? (
                                <div className="flex justify-center items-center h-full p-6">
                                    <p className="font-semibold text-gray-600 text-xl sm:text-md">
                                        {searchMode === "quick" && query.trim()
                                            ? "No Match !!"
                                            : searchMode === "deep"
                                                ? "No Records !!"
                                                : `No ${pageMode === "serviceFee" ? "Service Fee" : "Expense"} !!`}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <table className="w-full bg-white">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {[
                                                    "#",
                                                    "ID",
                                                    "Date",
                                                    `${pageMode === "serviceFee" ? "Client Address" : "Type"}`,
                                                    "Amount",
                                                    ...(pageMode === "expense" ? ["Note"] : []),
                                                    "Created",
                                                    "Action",
                                                ].map((header, index) => (
                                                    <th
                                                        key={index}
                                                        className="p-3 text-sm font-semibold text-gray-600 text-center border-b-2 border-gray-200"
                                                    >
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {paginatedData.map((data, index) => (
                                                <tr key={data._id || index} className="hover:bg-gray-50 even:bg-gray-100">
                                                    <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                        {(currentPage - 1) * itemsPerPage + index + 1}
                                                    </td>
                                                    <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                        {pageMode === "serviceFee" ? `${data.serviceFeeId}` : `${data.expenseId}` || "-"}
                                                    </td>
                                                    <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                        {formatTableDateTime(data.date)}
                                                    </td>
                                                    <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                        {pageMode === "serviceFee" ? `${data?.clientAddress || '-'}` : `${formatLabel(data.type)}` || "-"}
                                                    </td>
                                                    <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                        PKR {formatCurrency(data.amount)}
                                                    </td>
                                                    {pageMode === "expense" && (
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {data.note || "-"}
                                                        </td>
                                                    )}
                                                    <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                        {formatTableDateTime(data.createdAt)}
                                                    </td>
                                                    <td className="p-3 text-center border-b border-gray-200">
                                                        <div className="flex justify-center space-x-2">
                                                            <button
                                                                type="button"
                                                                data-menu-trigger="true"
                                                                className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (menuState.open && menuState.rowId === data._id) {
                                                                        closeMenu();
                                                                        return;
                                                                    }
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    openMenu({
                                                                        id: data._id,
                                                                        row: data,
                                                                        position: {
                                                                            top: rect.bottom + window.scrollY - 140,
                                                                            left: rect.right + window.scrollX - 180,
                                                                        },
                                                                    });
                                                                }}
                                                            >
                                                                <EllipsisVerticalIcon className="h-6 w-6 text-gray-600" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                        </div>

                        {/* Pagination */}
                        {searchMode === "quick" && totalPages > 1 && (
                            <div className="w-full flex flex-col sm:flex-row justify-between items-center p-4 gap-2 sm:gap-4">
                                <span className="text-sm text-gray-600 text-center sm:text-left">
                                    Showing {startIndex} to {endIndex} of {totalBackendPg} entries
                                </span>
                                {/* Pagination Buttons */}
                                <div className="flex flex-wrap justify-center sm:justify-end gap-1 sm:gap-2">
                                    {/* Previous Button */}
                                    <button
                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        disabled={loaders.fetch || currentPage === 1}
                                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                    </button>
                                    {/* Page Numbers */}
                                    {(() => {
                                        const buttons = [];
                                        const pageRange = 1; // current ±2 pages
                                        let startPage = Math.max(currentPage - pageRange, 1);
                                        let endPage = Math.min(currentPage + pageRange, totalPages);

                                        if (startPage > 1) {
                                            buttons.push(
                                                <button
                                                    key={1}
                                                    onClick={() => setCurrentPage(1)}
                                                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                                                >
                                                    1
                                                </button>
                                            );
                                            if (startPage > 2) buttons.push(<span key="start-ellipsis" className="px-2 hidden sm:inline">...</span>);
                                        }

                                        for (let i = startPage; i <= endPage; i++) {
                                            buttons.push(
                                                <button
                                                    key={i}
                                                    onClick={() => setCurrentPage(i)}
                                                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                                                >
                                                    {i}
                                                </button>
                                            );
                                        }

                                        if (endPage < totalPages) {
                                            if (endPage < totalPages - 1) buttons.push(<span key="end-ellipsis" className="px-2 hidden sm:inline">...</span>);
                                            buttons.push(
                                                <button
                                                    key={totalPages}
                                                    onClick={() => setCurrentPage(totalPages)}
                                                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === totalPages ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                                                >
                                                    {totalPages}
                                                </button>
                                            );
                                        }
                                        return buttons;
                                    })()}
                                    {/* Next Button */}
                                    <button
                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                        disabled={loaders.fetch || currentPage === totalPages}
                                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    )
}

export default Finance;